import Typography from '@mui/material/Typography';
import {
  deleteV1CompaniesCompanyIdLoadsId,
  deleteV1CompaniesCompanyIdTicketsId,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import ConfirmationDialog, {
  ConfirmationDialogProps,
} from '~components/Dialog/ConfirmationDialog';
import { Ticket } from '~hooks/useTickets/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { isNotFoundError } from '~utils/extractAxiosError';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

type ConfirmationDialogState =
  | { isOpen: false }
  | ({ isOpen: true } & Pick<
      ConfirmDeleteLoadAndTicketsDialogProps,
      'onClose' | 'onConfirm'
    >);

interface UseDeleteLoadAndTicketsProps {
  onLoadDeleted: (loadId: string) => void;
  onTicketsDeleted: (ticketIds: string[]) => void;
}

export function useDeleteLoadAndTickets({
  onLoadDeleted,
  onTicketsDeleted,
}: UseDeleteLoadAndTicketsProps) {
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmationDialogState, setConfirmationDialogState] =
    useState<ConfirmationDialogState>({ isOpen: false });

  const deleteLoadAndTickets = async (tickets: Ticket[], loadId: string) => {
    const loadTickets = tickets.filter((ticket) => ticket.loadId === loadId);
    const idsOfDeletedTickets: string[] = [];
    let errorToThrow: unknown;

    setIsDeleting(true);

    const deleteTicketsPromises = loadTickets.map((ticket) => {
      return new Promise<void>((resolve) => {
        deleteV1CompaniesCompanyIdTicketsId({
          path: { 'company-id': companyId, id: ticket.id },
        })
          .then(() => {
            idsOfDeletedTickets.push(ticket.id);

            resolve();
          })
          .catch((error) => {
            if (!errorToThrow) {
              errorToThrow = error;
            }

            resolve();
          });
      });
    });

    await Promise.all(deleteTicketsPromises);

    if (errorToThrow) {
      // if some tickets failed to delete, throw an error but still refresh the UI
      if (idsOfDeletedTickets.length) {
        onTicketsDeleted(idsOfDeletedTickets);
      }

      setIsDeleting(false);
      connection.handleRequestError(
        errorToThrow,
        t('error_messages.tickets.failed_to_delete_ticket'),
      );
    }

    try {
      await deleteV1CompaniesCompanyIdLoadsId({
        path: { 'company-id': companyId, id: loadId },
      });
    } catch (error) {
      const loadAlreadyDeleted = isNotFoundError(error);

      if (!loadAlreadyDeleted) {
        // tickets were deleted but the load did not, so we still need to refresh the UI
        if (idsOfDeletedTickets.length) {
          onTicketsDeleted(idsOfDeletedTickets);
        }

        setIsDeleting(false);
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_remove_load'),
        );
      }
    }

    onTicketsDeleted(idsOfDeletedTickets);
    onLoadDeleted(loadId);
    toasterStore.push(alert(t('loads.load_and_ticket_removed'), AlertTypes.success));
    setIsDeleting(false);

    return true;
  };

  const deleteLoadOrAlertIfItHasAssociatedTickets = async (
    tickets: Ticket[],
    loadId: string,
  ) => {
    const loadTickets = tickets.filter((ticket) => ticket.loadId === loadId);
    const loadHasAssociatedTickets = loadTickets.length > 0;

    if (loadHasAssociatedTickets) {
      setConfirmationDialogState({
        isOpen: true,
        onClose: () => {
          setConfirmationDialogState({ isOpen: false });
        },
        onConfirm: async () => {
          try {
            await deleteLoadAndTickets(tickets, loadId);
          } finally {
            setConfirmationDialogState({ isOpen: false });
          }
        },
      });

      return;
    }

    setIsDeleting(true);

    try {
      await deleteV1CompaniesCompanyIdLoadsId({
        path: { 'company-id': companyId, id: loadId },
      });
    } catch (error) {
      const loadAlreadyDeleted = isNotFoundError(error);

      if (!loadAlreadyDeleted) {
        setIsDeleting(false);
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_remove_load'),
        );
      }
    }

    onLoadDeleted(loadId);
    toasterStore.push(alert(t('loads.load_removed'), AlertTypes.success));
    setIsDeleting(false);

    return true;
  };

  return {
    confirmationDialogState,
    deleteLoadOrAlertIfItHasAssociatedTickets,
    isDeleting,
  };
}

type ConfirmDeleteLoadAndTicketsDialogProps = Pick<
  ConfirmationDialogProps,
  'isOpen' | 'onClose'
> & { onConfirm: () => Promise<void> };

export function ConfirmDeleteLoadAndTicketsDialog({
  isOpen,
  onClose,
  onConfirm,
}: ConfirmDeleteLoadAndTicketsDialogProps) {
  const [isBusy, setIsBusy] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsBusy(true);
      await onConfirm();
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <ConfirmationDialog
      isBusy={isBusy}
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={t('loads.remove_load_and_ticket')}
    >
      <Typography>{t('loads.remove_load_and_ticket_confirmation')}</Typography>
    </ConfirmationDialog>
  );
}
